import React, { useEffect, useRef, useState } from 'react';
import { useFeatures } from '../utils/features';
import { findMaterial, currentActor } from '../utils/beacons';
import FindProgress from './FindProgress';

const sheets = (n) => `${n} sheet${Number(n) === 1 ? '' : 's'}`;
const shortDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return y && m && d ? `${m}/${d}/${String(y).slice(2)}` : null;
};
const earmarkText = (p) => (p && p.earmarks && p.earmarks.length
  ? p.earmarks.map((e) => `${e.order_number}${e.customer_name ? ` (${e.customer_name})` : ''}`).join(', ')
  : null);
const palletLine = (p) => [
  `TRK ${p.trk}`,
  `${sheets(p.remaining)} left`,
  shortDate(p.received_date) ? `received ${shortDate(p.received_date)}` : null,
  p.beacon_id || null,
].filter(Boolean).join(' · ');

/**
 * Laser workstation "Find Material" (Mike, 2026-10-13): one pallet open at a time, so it
 * flashes ONE pallet, the one the laser should use next. JobQ picks it
 * (pallet_beacons.pick_next_pallet): the pallet plugged in at the laser wins (nothing flashes);
 * an order's earmarked pallets go to that order's jobs; started pallets fewest-left first; then
 * the oldest received. A next pallet with no beacon is named, never skipped. Renders nothing
 * unless GET /features says pallet_beacons is on. Advisory: a failed call shows a message.
 */
export default function FindMaterialButton({ taskId }) {
  const enabled = useFeatures().palletBeacons.enabled;
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);     // the API's pick
  const [msg, setMsg] = useState(null);
  const shownTask = useRef(taskId);
  shownTask.current = taskId;

  useEffect(() => {
    setAnswer(null); setMsg(null);
  }, [taskId]);

  if (!enabled || !taskId) return null;

  const handleFind = async () => {
    const forTask = taskId;
    setBusy(true);
    setMsg(null);
    try {
      const r = await findMaterial(forTask, { actor: currentActor() });
      if (shownTask.current !== forTask) return;
      if (!r.ok || !r.body) {
        setAnswer(null);
        setMsg(`Couldn't find the material: ${r.message}`);
        return;
      }
      setAnswer(r.body);
    } finally {
      setBusy(false);
    }
  };

  const a = answer;
  const p = a && a.pallet;
  // An older API answers the find without command_id / progress: say what it will do instead.
  const followable = !!(a && a.find && a.find.command_id && a.find.progress);
  const findStatus = () => {
    if (!a || !a.find || followable) return null;
    return a.find.in_range
      ? 'Will flash within a few seconds'
      : 'Not heard lately: flashes when a gateway reaches it (for 5 min)';
  };

  let headline = null;
  if (a) {
    if (a.state === 'docked') {
      headline = { tone: 'info', title: 'Use the pallet plugged in at the laser', body: palletLine(p) };
    } else if (a.state === 'next' && a.find) {
      headline = { tone: 'success', title: `Flashing the next pallet: ${a.find.beacon_id}`, body: palletLine(p), status: findStatus() };
    } else if (a.state === 'next') {
      headline = { tone: 'warning', title: 'Next pallet has no beacon: look for its label', body: palletLine(p) };
    } else if (a.state === 'earmarked_only') {
      const first = a.skipped_earmarked && a.skipped_earmarked[0];
      headline = {
        tone: 'warning',
        title: 'Only pallets earmarked for other orders are left',
        body: first ? `${palletLine(first)} · for ${earmarkText(first)}. Check before using one.` : null,
      };
    } else {
      headline = { tone: 'secondary', title: 'No open pallet of this material in JobQ', body: a.reason || null };
    }
  }

  const docked = a && a.docked_ineligible && a.docked_ineligible[0];
  const blockedIds = new Set(((a && a.skipped_blocked) || []).map((l) => l.mtn_id));

  return (
    <div style={{ marginTop: '8px' }} data-testid="find-material">
      <button
        type="button"
        onClick={handleFind}
        disabled={busy}
        style={{
          backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px',
          padding: '0.4rem 0.9rem', cursor: busy ? 'wait' : 'pointer', fontWeight: 'bold', opacity: busy ? 0.7 : 1,
        }}
      >
        <i className="bi bi-lightbulb me-1"></i>{busy ? 'Finding…' : 'Find Material'}
      </button>
      {msg && <div className="small mt-1 text-danger">{msg}</div>}
      {headline && (
        <div className={`small mt-2 p-2 rounded border border-${headline.tone}-subtle bg-${headline.tone}-subtle`} data-testid="find-material-answer">
          <div className="fw-bold">{headline.title}</div>
          {headline.body && <div data-testid="find-material-pallet">{headline.body}</div>}
          {headline.status && <div className="text-success" data-testid="find-material-status">{headline.status}</div>}
          {followable && a.state === 'next' && (
            <FindProgress find={a.find} onRetry={handleFind} />
          )}
          {p && earmarkText(p) && <div>Earmarked for this order: {earmarkText(p)}</div>}
          {p && !a.enough && (
            <div data-testid="find-material-then">
              {`This job needs ${sheets(a.sheets_needed)}; this pallet has ${p.remaining}.`}
              {a.then ? ` After it: TRK ${a.then.trk}${a.then.beacon_id ? ` (${a.then.beacon_id})` : ' (no beacon)'}.` : ''}
            </div>
          )}
          {docked && (
            <div className="text-danger">
              {`The pallet plugged in at the laser (TRK ${docked.trk}) is not for this job: `}
              {blockedIds.has(docked.mtn_id) ? 'it has a do-not-use note.' : `it is earmarked for ${earmarkText(docked)}.`}
            </div>
          )}
          {a.skipped_blocked && a.skipped_blocked.length > 0 && (
            <div className="text-muted">
              {`Skipped (do-not-use note): ${a.skipped_blocked.map((l) => `TRK ${l.trk}`).join(', ')}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
